// Mode-button and menu icons from the approved mockups.
const box = (stroke: string) => (
  <rect x="1" y="1" width="28" height="28" rx="5" fill="none" stroke={stroke} strokeWidth="1.5" />
);
const font = {
  fontFamily: 'Inter, sans-serif',
  textAnchor: 'middle',
  dominantBaseline: 'central',
} as const;

export function DigitIcon({ on }: { on: boolean }) {
  const ink = on ? '#FFFFFF' : '#3D3A34';
  return (
    <svg width="30" height="30" viewBox="0 0 30 30" aria-hidden="true">
      {box(ink)}
      <text x="15" y="15.5" {...font} fontSize="18" fontWeight="600" fill={ink}>
        1
      </text>
    </svg>
  );
}

export function CornerIcon({ on }: { on: boolean }) {
  const ink = on ? '#FFFFFF' : '#3D3A34';
  return (
    <svg width="30" height="30" viewBox="0 0 30 30" aria-hidden="true">
      {box(ink)}
      <g {...font} fontSize="8" fontWeight="700" fill={ink}>
        <text x="7" y="7.5">
          1
        </text>
        <text x="23" y="7.5">
          2
        </text>
        <text x="7" y="22.5">
          3
        </text>
      </g>
    </svg>
  );
}

export function CentreIcon({ on }: { on: boolean }) {
  const ink = on ? '#FFFFFF' : '#3D3A34';
  return (
    <svg width="30" height="30" viewBox="0 0 30 30" aria-hidden="true">
      {box(ink)}
      <text x="15" y="15.5" {...font} fontSize="9" fontWeight="700" fill={ink}>
        123
      </text>
    </svg>
  );
}

export function PencilIcon({ on }: { on: boolean }) {
  const ink = on ? '#FFFFFF' : '#3D3A34';
  return (
    <svg width="30" height="30" viewBox="0 0 30 30" aria-hidden="true">
      {box(ink)}
      <g {...font} fontSize="7" fontWeight="700" fill={ink}>
        <text x="6.5" y="6.5">
          1
        </text>
        <text x="23.5" y="6.5">
          3
        </text>
        <text x="15" y="15">
          5
        </text>
        <text x="23.5" y="23.5">
          9
        </text>
      </g>
    </svg>
  );
}

/** Fill all candidates (next to redo): every digit as marks in the selected cells. */
export function FillIcon() {
  const ink = '#3D3A34';
  return (
    <svg width="30" height="30" viewBox="0 0 30 30" aria-hidden="true">
      {box(ink)}
      <text x="15" y="15.5" {...font} fontSize="9" fontWeight="700" fill={ink}>
        1–9
      </text>
    </svg>
  );
}

/** The select tool (Marko's pick, F): a box with cells 1, 3, 4 and 9 filled, apart. */
export function SelectIcon({ on }: { on: boolean }) {
  const ink = on ? '#FFFFFF' : '#3D3A34';
  return (
    <svg width="30" height="30" viewBox="0 0 30 30" aria-hidden="true">
      {box(ink)}
      <path
        d="M10.33 2v26M19.67 2v26M2 10.33h26M2 19.67h26"
        stroke={ink}
        strokeWidth="1"
        fill="none"
      />
      <g fill={ink}>
        <rect x="3" y="3" width="6.3" height="6.3" rx="1" />
        <rect x="20.7" y="3" width="6.3" height="6.3" rx="1" />
        <rect x="3" y="11.4" width="6.3" height="7.2" rx="1" />
        <rect x="20.7" y="20.7" width="6.3" height="6.3" rx="1" />
      </g>
    </svg>
  );
}

export function ColourIcon({ on }: { on: boolean }) {
  return (
    <svg width="30" height="30" viewBox="0 0 30 30" aria-hidden="true">
      <rect x="2" y="2" width="13" height="13" fill="#F4B183" />
      <rect x="15" y="2" width="13" height="13" fill="#9ED8A8" />
      <rect x="2" y="15" width="13" height="13" fill="#A9C1F2" />
      <rect x="15" y="15" width="13" height="13" fill="#F2A7C3" />
      {box(on ? '#FFFFFF' : '#3D3A34')}
    </svg>
  );
}

export function PenIcon({ on }: { on: boolean }) {
  const ink = on ? '#FFFFFF' : '#3D3A34';
  return (
    <svg width="30" height="30" viewBox="0 0 30 30" aria-hidden="true">
      {box(ink)}
      <path
        d="M7 22C11 9 19 21 23 8"
        fill="none"
        stroke={ink}
        strokeWidth="2"
        strokeLinecap="round"
      />
      <circle cx="7" cy="22" r="2.2" fill={ink} />
      <circle cx="23" cy="8" r="2.2" fill={ink} />
    </svg>
  );
}

export function DeleteIcon() {
  return (
    <svg
      width="26"
      height="26"
      viewBox="0 0 24 24"
      fill="none"
      stroke="currentColor"
      strokeWidth="2"
      strokeLinecap="round"
      strokeLinejoin="round"
      aria-hidden="true"
    >
      <path d="M21 5H9l-6 7 6 7h12z" />
      <path d="M17 9l-6 6M11 9l6 6" />
    </svg>
  );
}

function Stroke({
  children,
  size = 18,
  width = 2.2,
}: {
  children: React.ReactNode;
  size?: number;
  width?: number;
}) {
  return (
    <svg
      width={size}
      height={size}
      viewBox="0 0 24 24"
      fill="none"
      stroke="currentColor"
      strokeWidth={width}
      strokeLinecap="round"
      strokeLinejoin="round"
      aria-hidden="true"
    >
      {children}
    </svg>
  );
}

export const UndoIcon = () => (
  <Stroke>
    <path d="M9 14L4 9l5-5" />
    <path d="M4 9h10a6 6 0 0 1 0 12h-3" />
  </Stroke>
);
export const RedoIcon = () => (
  <Stroke>
    <path d="M15 14l5-5-5-5" />
    <path d="M20 9H10a6 6 0 0 0 0 12h3" />
  </Stroke>
);
export const CogIcon = () => (
  <Stroke>
    <circle cx="12" cy="12" r="3" />
    <path d="M19.4 15a1.65 1.65 0 0 0 .33 1.82l.06.06a2 2 0 1 1-2.83 2.83l-.06-.06a1.65 1.65 0 0 0-1.82-.33 1.65 1.65 0 0 0-1 1.51V21a2 2 0 1 1-4 0v-.09A1.65 1.65 0 0 0 9 19.4a1.65 1.65 0 0 0-1.82.33l-.06.06a2 2 0 1 1-2.83-2.83l.06-.06A1.65 1.65 0 0 0 4.68 15a1.65 1.65 0 0 0-1.51-1H3a2 2 0 1 1 0-4h.09A1.65 1.65 0 0 0 4.6 9a1.65 1.65 0 0 0-.33-1.82l-.06-.06a2 2 0 1 1 2.83-2.83l.06.06A1.65 1.65 0 0 0 9 4.68a1.65 1.65 0 0 0 1-1.51V3a2 2 0 1 1 4 0v.09a1.65 1.65 0 0 0 1 1.51 1.65 1.65 0 0 0 1.82-.33l.06-.06a2 2 0 1 1 2.83 2.83l-.06.06A1.65 1.65 0 0 0 19.4 9a1.65 1.65 0 0 0 1.51 1H21a2 2 0 1 1 0 4h-.09a1.65 1.65 0 0 0-1.51 1z" />
  </Stroke>
);
/** The helper (Σ): the combinations of a cage or an arrow (Marko's pick, 2026-11-03). */
export const SumIcon = () => (
  <Stroke>
    <path d="M17.5 5H6.5l6 7-6 7h11" />
  </Stroke>
);
/** Restart, at the size of the undo and redo icons. */
export const ResetIcon = () => (
  <Stroke>
    <path d="M3 12a9 9 0 1 0 3-6.7" />
    <path d="M3 4v5h5" />
  </Stroke>
);
export const PauseIcon = () => (
  <svg
    width="12"
    height="12"
    viewBox="0 0 24 24"
    fill="none"
    stroke="currentColor"
    strokeWidth="3"
    strokeLinecap="round"
    aria-hidden="true"
  >
    <path d="M8 5v14M16 5v14" />
  </svg>
);
export const PlayIcon = () => (
  <svg width="12" height="12" viewBox="0 0 24 24" fill="currentColor" aria-hidden="true">
    <path d="M7 4l13 8-13 8z" />
  </svg>
);
export const DotsIcon = () => (
  <Stroke size={20} width={3}>
    <path d="M5 12h.01M12 12h.01M19 12h.01" />
  </Stroke>
);
export const ChevronDown = () => (
  <Stroke size={18} width={2}>
    <path d="M6 9l6 6 6-6" />
  </Stroke>
);
export const BulbIcon = () => (
  <Stroke size={22} width={2}>
    <path d="M9 18h6M10 21h4M12 3a6 6 0 0 0-4 10.5c.8.8 1 1.5 1 2.5h6c0-1 .2-1.7 1-2.5A6 6 0 0 0 12 3z" />
  </Stroke>
);
export const CheckIcon = () => (
  <Stroke size={22} width={2}>
    <path d="M5 12l5 5 9-10" />
  </Stroke>
);
export const RestartIcon = () => (
  <Stroke size={22} width={2}>
    <path d="M3 12a9 9 0 1 0 3-6.7" />
    <path d="M3 4v5h5" />
  </Stroke>
);
export const RulesIcon = () => (
  <Stroke size={22} width={2}>
    <rect x="5" y="3" width="14" height="18" rx="2" />
    <path d="M9 8h6M9 12h6" />
  </Stroke>
);
export const SlidersIcon = () => (
  <Stroke size={22} width={2}>
    <path d="M4 7h10M18 7h2M4 17h4M12 17h8" />
    <circle cx="16" cy="7" r="2" />
    <circle cx="10" cy="17" r="2" />
  </Stroke>
);
export const LinkIcon = () => (
  <Stroke size={22} width={2}>
    <path d="M10 14a4 4 0 0 0 6 0l3-3a4 4 0 0 0-6-6l-1 1" />
    <path d="M14 10a4 4 0 0 0-6 0l-3 3a4 4 0 0 0 6 6l1-1" />
  </Stroke>
);
export const PlusIcon = () => (
  <Stroke size={22} width={2}>
    <path d="M12 5v14M5 12h14" />
  </Stroke>
);
/** Zoom reset: four corners (fit the whole grid). */
export const FitIcon = () => (
  <Stroke size={20} width={2.2}>
    <path d="M4 9V4h5M20 9V4h-5M4 15v5h5M20 15v5h-5" />
  </Stroke>
);
